import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Link2,
  Search,
  Upload,
  X,
} from "lucide-react";
import { useState } from "react";

import { BandaDePipeline } from "@/components/documentos/banda-pipeline";
import { BarraDeLote } from "@/components/documentos/mover";
import { EmptyState } from "@/components/feedback/empty-state";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Tile } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// ===========================================================================
// Los expedientes que entrega finanzas, y qué le falta a cada uno.
//
// Un expediente es la boleta de venta del local, los documentos de la unidad
// vendida, el voucher del pago y la ficha con el DNI del comprador. La pregunta
// del área es una sola —«¿cuáles llegaron completos?»— y hasta ahora esta
// pantalla contestaba con contadores de imágenes: vouchers encontrados,
// imágenes distintas, repetidos descartados. Eso es contabilidad de la máquina.
//
// El cuadro que sí responde ya existía, pero solo se alcanzaba desde el enlace
// que sale UNA vez justo después de subir un lote. Aquí vive para el mes.
// ===========================================================================

const POR_PAGINA = 100;

type Expediente = {
  archivoId: string;
  nombre: string;
  estado: string;
  serieNumero: string | null;
  cliente: string | null;
  total: number | null;
  moneda: "PEN" | "USD" | null;
  paginas: number | null;
  tieneComprobante: boolean;
  tieneFicha: boolean;
  vouchers: number;
  completo: boolean;
  falta: string | null;
  /** El cliente del nombre del fichero, cuando NO es el del papel. */
  clienteDelNombre: string | null;
  /** Cuántos comprobantes trae. Más de uno es la venta corregida. */
  comprobantes: number;
  /** De qué razón social del grupo es. Es por lo que se agrupa la lista. */
  razonSocial: string | null;
  razonSocialDeclarada: string | null;
  razonSocialDelPapel: string | null;
  /** Se declaró una razón social al subir y el papel dice otra. */
  discrepaRazonSocial: boolean;
  /** Tiene un cruce contra un abono. Ni se mueve de mes ni se borra. */
  conciliado: boolean;
};

/**
 * LA LISTA, AGRUPADA POR RAZÓN SOCIAL.
 *
 * En la carga se declara a qué razón social pertenece lo que entra, y el grupo
 * tiene ocho. Sin agrupar, cien expedientes de varias empresas son una lista
 * plana donde no se ve de quién es qué — y de quién es qué es justamente lo que
 * decide contra qué cuentas de banco se cruza.
 *
 * El orden de los grupos es el del propio listado —que ya viene ordenado por
 * nombre de fichero— así que agrupar no reordena nada por dentro.
 */
function porRazonSocial(
  expedientes: Expediente[],
): Array<{ razonSocial: string; suyos: Expediente[] }> {
  const grupos = new Map<string, Expediente[]>();
  for (const e of expedientes) {
    const clave = e.razonSocial ?? "Sin razón social declarada";
    grupos.set(clave, [...(grupos.get(clave) ?? []), e]);
  }
  return [...grupos.entries()].map(([razonSocial, suyos]) => ({
    razonSocial,
    suyos,
  }));
}

type Filtro =
  | "todos"
  | "incompletos"
  | "sinVoucher"
  | "sinFicha"
  | "fallados"
  /** Los que no cuadran consigo mismos: otro cliente, o más de un comprobante. */
  | "discrepan";

const SIMBOLO = { PEN: "S/", USD: "US$" } as const;

const dinero = (c: number | null, m: "PEN" | "USD" | null) =>
  c === null
    ? "—"
    : `${m ? SIMBOLO[m] : ""} ${(c / 100).toLocaleString("es-PE", {
        minimumFractionDigits: 2,
      })}`.trim();

export function Expedientes() {
  const [mes, setMes] = useState<string | undefined>(undefined);
  /**
   * SE ABRE EN «TODOS», siempre.
   *
   * Abría en «incompletos» cuando los había, y era desconcertante: se entra a
   * la pantalla de documentos para ver los documentos, y aparecía una lista
   * corta que no se parecía a lo cargado, sin nada que dijera que había un
   * filtro puesto. Lo que falta sigue estando a un clic, con su número en la
   * pestaña y en el recuadro de arriba.
   */
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [busca, setBusca] = useState("");
  const [aplicada, setAplicada] = useState("");
  const [pagina, setPagina] = useState(0);
  /**
   * LO ELEGIDO PARA MOVER O BORRAR EN LOTE.
   *
   * El error más común de la carga es el mes: se sueltan treinta expedientes y
   * el mes declarado se quedó en el anterior. Corregirlos de a uno son treinta
   * viajes, así que la unidad de trabajo aquí es el lote.
   *
   * Se vacía al cambiar de mes, de filtro, de página o de búsqueda: lo que no
   * se ve no se puede seguir eligiendo, y borrar algo que ya no está en
   * pantalla es exactamente el accidente que hay que evitar.
   */
  const [elegidos, setElegidos] = useState<string[]>([]);
  const vaciarSeleccion = () => setElegidos([]);

  const efectivo: Filtro = filtro;
  const { data, isPending } = api.expedientes.useQuery(
    ["expedientes", mes ?? "ultimo", efectivo, aplicada, String(pagina)],
    {
      query: {
        periodo: mes,
        filtro: efectivo,
        buscar: aplicada || undefined,
        limite: POR_PAGINA,
        offset: pagina * POR_PAGINA,
      },
    },
  );
  const d = data?.status === 200 ? data.body : null;
  const n = (v: number) => v.toLocaleString("es-PE");
  const paginas = d ? Math.ceil(d.filtradas / POR_PAGINA) : 0;

  const cambiarFiltro = (f: Filtro) => {
    setFiltro(f);
    setPagina(0);
    vaciarSeleccion();
  };

  // Los que SE PUEDEN elegir de la página: lo conciliado queda fuera de
  // «elegir todos», que si no marcaría cosas que el servidor va a rechazar.
  const sueltosEnPagina = ((d?.expedientes ?? []) as Expediente[])
    .filter((e) => !e.conciliado)
    .map((e) => e.archivoId);
  const elegido = new Set(elegidos);
  const todosElegidos =
    sueltosEnPagina.length > 0 &&
    sueltosEnPagina.every((id) => elegido.has(id));
  const alternar = (id: string) =>
    setElegidos((antes) =>
      antes.includes(id) ? antes.filter((x) => x !== id) : [...antes, id],
    );
  /** Marcar o desmarcar un grupo entero: lo conciliado nunca entra. */
  const alternarGrupo = (suyos: Expediente[]) => {
    const sueltos = suyos.filter((e) => !e.conciliado).map((e) => e.archivoId);
    const todosPuestos = sueltos.every((id) => elegido.has(id));
    setElegidos((antes) =>
      todosPuestos
        ? antes.filter((id) => !sueltos.includes(id))
        : [...new Set([...antes, ...sueltos])],
    );
  };

  return (
    <>
      <Cabecera
        migas={["Documentos"]}
        titulo="Documentos de clientes"
        acciones={
          <>
            {d && d.periodos.length > 0 ? (
              <select
                aria-label="Mes"
                className="rounded-md border bg-background px-2.5 py-1.5 text-[12.5px]"
                value={d.periodo}
                onChange={(e) => {
                  setMes(e.target.value);
                  setPagina(0);
                  vaciarSeleccion();
                }}
              >
                {d.periodos.map((x: { id: string; nombre: string }) => (
                  <option key={x.id} value={x.nombre}>
                    {x.nombre}
                  </option>
                ))}
              </select>
            ) : null}
            <Button size="sm" asChild>
              <Link to="/carga">
                <Upload className="mr-2 size-4" />
                Cargar el mes
              </Link>
            </Button>
          </>
        }
      />

      {isPending || !d ? (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-24" />
          ))}
        </div>
      ) : d.archivos === 0 ? (
        <EmptyState
          title="Todavía no entró ningún expediente"
          message="Acá se ve, expediente por expediente, si llegó completo: la boleta, el voucher del pago y la ficha del comprador. Se llena en cuanto se sube el primero."
          action={
            <Button asChild>
              <Link to="/carga">
                <Upload className="mr-2 size-4" />
                Cargar el mes
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          {/* Aquí la lectura SÍ significa algo: los vouchers están en los
              expedientes, y es donde se decide pagarle al modelo. */}
          <BandaDePipeline clase="comprobante" />

          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <Tile titulo="Expedientes" valor={n(d.archivos)} tono="neutro" />
            <Tile
              titulo="Completos"
              subtitulo="con boleta y con voucher"
              valor={n(d.completos)}
              tono={d.completos === d.archivos ? "ok" : "neutro"}
            />
            <Tile
              titulo="Incompletos"
              subtitulo="es lo que hay que pedir"
              valor={n(d.incompletos)}
              tono={d.incompletos > 0 ? "warn" : "ok"}
            />
            <Tile
              titulo="No se pudieron leer"
              subtitulo={
                d.enCurso > 0 ? `${n(d.enCurso)} todavía en la cola` : undefined
              }
              valor={n(d.fallados)}
              tono={d.fallados > 0 ? "bad" : "ok"}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap rounded-md border p-0.5">
              {(
                [
                  ["incompletos", `Incompletos (${d.incompletos})`],
                  ["sinVoucher", "Sin voucher"],
                  ["sinFicha", "Sin ficha"],
                  ["fallados", "No se pudieron leer"],
                  // Los que piden que alguien los mire aunque estén completos: el
                  // fichero dice una persona y el papel otra, o dentro hay más
                  // de un comprobante.
                  [
                    "discrepan",
                    `No cuadran${d.discrepan ? ` (${d.discrepan})` : ""}`,
                  ],
                  ["todos", "Todos"],
                ] as const
              ).map(([v, texto]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => cambiarFiltro(v)}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium text-[12px] transition-colors",
                    efectivo === v
                      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {texto}
                </button>
              ))}
            </div>
            {/* La búsqueda va contra el servidor y con Enter, no según se
                escribe: son 150 expedientes y una consulta por tecla es
                trabajo que nadie pidió. */}
            <form
              className="flex min-w-[240px] flex-1 items-center gap-2 rounded-md border px-2.5"
              onSubmit={(e) => {
                e.preventDefault();
                setPagina(0);
                setAplicada(busca.trim());
                vaciarSeleccion();
              }}
            >
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Cliente, serie-número o nombre del archivo"
                className="w-full bg-transparent py-1.5 text-[12.5px] focus:outline-none"
              />
              {aplicada ? (
                <button
                  type="button"
                  className="text-[11.5px] text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setBusca("");
                    setAplicada("");
                    setPagina(0);
                    vaciarSeleccion();
                  }}
                >
                  limpiar
                </button>
              ) : null}
            </form>
          </div>

          {d.expedientes.length === 0 ? (
            <p className="py-10 text-center text-muted-foreground text-sm">
              {efectivo === "incompletos"
                ? "Ningún expediente del mes está incompleto."
                : "Ningún expediente coincide con ese filtro."}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b bg-muted/30 text-[10.5px] text-muted-foreground uppercase tracking-wider">
                    <th className="w-9 px-3 py-2">
                      <Casilla
                        etiqueta="Elegir todos los de la página"
                        puestos={elegidos.length}
                        total={sueltosEnPagina.length}
                        alCambiar={() =>
                          setElegidos(todosElegidos ? [] : sueltosEnPagina)
                        }
                      />
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      Serie-número
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Cliente
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Total
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Boleta
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Ficha
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Vouchers
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      Qué falta
                    </th>
                  </tr>
                </thead>
                {porRazonSocial(d.expedientes as Expediente[]).map((g) => (
                  <tbody key={g.razonSocial}>
                    {/* La cabecera del grupo. Con una sola razón social sobra,
                      pero se pinta igual: quien abre la pantalla tiene que
                      saber de quién es lo que está mirando sin deducirlo. */}
                    <tr className="border-b bg-muted/60">
                      <td className="w-9 px-3 py-1.5">
                        {/* Marcar la razón social entera: el lote mal cargado
                            casi siempre es el de una sola empresa. */}
                        <Casilla
                          etiqueta={`Elegir los de ${g.razonSocial}`}
                          puestos={
                            g.suyos.filter(
                              (e) => !e.conciliado && elegido.has(e.archivoId),
                            ).length
                          }
                          total={g.suyos.filter((e) => !e.conciliado).length}
                          alCambiar={() => alternarGrupo(g.suyos)}
                        />
                      </td>
                      <td colSpan={8} className="px-4 py-1.5">
                        <span className="font-semibold text-[12px]">
                          {g.razonSocial}
                        </span>
                        <span className="ml-2 text-[11px] text-muted-foreground">
                          {g.suyos.length}{" "}
                          {g.suyos.length === 1 ? "expediente" : "expedientes"}
                        </span>
                      </td>
                    </tr>
                    {g.suyos.map((e) => (
                      <tr
                        key={e.archivoId}
                        className={cn(
                          "border-b last:border-0 hover:bg-muted/50",
                          !e.completo && "bg-amber-500/5",
                          elegido.has(e.archivoId) && "bg-sky-500/10",
                        )}
                      >
                        <td className="w-9 px-3 py-2">
                          {/* Lo conciliado no se elige: mover la boleta a otro
                              mes dejaría el cruce a caballo entre dos, y
                              borrarla lo dejaría apuntando a nada. */}
                          {e.conciliado ? (
                            <Link2
                              className="size-3.5 text-muted-foreground"
                              aria-label="Conciliado: no se mueve ni se borra"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              aria-label={`Elegir ${e.serieNumero ?? e.nombre}`}
                              className="align-middle"
                              checked={elegido.has(e.archivoId)}
                              onChange={() => alternar(e.archivoId)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-2 tabular-nums">
                          <Link
                            to="/archivo/$id"
                            params={{ id: e.archivoId }}
                            className="font-medium hover:underline"
                          >
                            {e.serieNumero ?? "—"}
                          </Link>
                          {!e.serieNumero ? (
                            <span
                              className="block max-w-[220px] truncate text-[11px] text-muted-foreground"
                              title={e.nombre}
                            >
                              {e.nombre.replace(/^.*\//, "")}
                            </span>
                          ) : null}
                        </td>
                        <td className="max-w-[280px] px-3 py-2">
                          <span className="block truncate">
                            {e.cliente ?? "—"}
                          </span>
                          {/* El fichero dice otra persona. Uno de los dos está
                            mal, y hasta ahora ninguna pantalla lo nombraba. */}
                          {e.clienteDelNombre ? (
                            <span
                              className="block truncate text-[11px] text-amber-700 dark:text-amber-500"
                              title={`El nombre del fichero dice ${e.clienteDelNombre}`}
                            >
                              el fichero dice {e.clienteDelNombre}
                            </span>
                          ) : null}
                          {e.comprobantes > 1 ? (
                            <span className="block text-[11px] text-amber-700 dark:text-amber-500">
                              trae {e.comprobantes} comprobantes
                            </span>
                          ) : null}
                          {/* Se declaró una razón social al subir y el papel
                              dice otra. Entra igual y queda marcado, como el
                              aviso del cliente del nombre del fichero. */}
                          {e.discrepaRazonSocial ? (
                            <span
                              className="block truncate text-[11px] text-amber-700 dark:text-amber-500"
                              title={`Se subió como ${e.razonSocialDeclarada} y la boleta la emitió ${e.razonSocialDelPapel}`}
                            >
                              se subió como {e.razonSocialDeclarada}, la emitió{" "}
                              {e.razonSocialDelPapel}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {dinero(e.total, e.moneda)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Senal si={e.tieneComprobante} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Senal si={e.tieneFicha} />
                        </td>
                        <td
                          className={cn(
                            "px-3 py-2 text-right tabular-nums",
                            e.vouchers === 0 &&
                              "font-semibold text-amber-700 dark:text-amber-500",
                          )}
                        >
                          {e.vouchers}
                        </td>
                        <td className="px-4 py-2">
                          {e.completo ? (
                            <span className="flex items-center gap-1.5 text-[11.5px] text-emerald-700 dark:text-emerald-500">
                              <Check className="size-3.5 shrink-0" />
                              Completo
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-[11.5px] text-amber-700 dark:text-amber-500">
                              <AlertTriangle className="size-3.5 shrink-0" />
                              {e.falta ?? "incompleto"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          )}

          <BarraDeLote
            elegidos={elegidos}
            meses={d.periodos}
            mesActual={d.periodo}
            alLimpiar={vaciarSeleccion}
          />

          {paginas > 1 ? (
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="text-muted-foreground tabular-nums">
                {(pagina * POR_PAGINA + 1).toLocaleString("es-PE")}–
                {Math.min(
                  (pagina + 1) * POR_PAGINA,
                  d.filtradas,
                ).toLocaleString("es-PE")}{" "}
                de {d.filtradas.toLocaleString("es-PE")}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pagina === 0}
                  onClick={() => {
                    setPagina((p) => Math.max(0, p - 1));
                    vaciarSeleccion();
                  }}
                >
                  <ChevronLeft className="size-3.5" />
                </Button>
                <span className="tabular-nums">
                  {pagina + 1} / {paginas}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pagina + 1 >= paginas}
                  onClick={() => {
                    setPagina((p) => p + 1);
                    vaciarSeleccion();
                  }}
                >
                  <ChevronRight className="size-3.5" />
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

/**
 * Una casilla que sabe decir «algunos».
 *
 * El estado a medias no es `checked` ni sin marcar: marcar la cabecera del
 * grupo cuando hay dos de cien elegidos hace creer que se van a mover los cien,
 * y esa confusión con «Borrar» al lado es cara. `indeterminate` solo se pone
 * desde JavaScript, de ahí el ref.
 */
function Casilla({
  etiqueta,
  puestos,
  total,
  alCambiar,
}: {
  etiqueta: string;
  puestos: number;
  total: number;
  alCambiar: () => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={etiqueta}
      className="align-middle"
      disabled={total === 0}
      checked={total > 0 && puestos === total}
      ref={(el) => {
        if (el) el.indeterminate = puestos > 0 && puestos < total;
      }}
      onChange={alCambiar}
    />
  );
}

/** Sí o no, con forma además de color: el color solo no se lee en gris. */
function Senal({ si }: { si: boolean }) {
  return si ? (
    <Check className="inline size-4 text-emerald-600 dark:text-emerald-500" />
  ) : (
    <X className="inline size-4 text-amber-600 dark:text-amber-500" />
  );
}
